// Lib
import { createSelector } from 'reselect';
import { flow, map, sortBy } from 'lodash/fp';

// Utils
import { propIn } from '../../common/utils/immutableHelper';
import { getUsers } from './usersSelector';
import { isStaff, isUserSubscriptionAdmin } from './userUtil';
import { getCurrentBoardId } from '../reducers/currentBoardId/currentBoardIdSelector';
import { getElements } from '../element/selectors/elementsSelector';
import { isElementOrAncestorInTrash } from '../../common/elements/utils/elementTraversalUtils';
import {
    getUserEnableDoubleClickToCreateNotes,
    getUserEnableDrawingInkEffect,
} from '../../common/users/utils/userPropertyUtils';

const mapWithKey = map.convert({ cap: false });

export const getCurrentUser = propIn(['app', 'currentUser']);
export const getCurrentUserId = (state) => state.getIn(['app', 'currentUser', '_id']);
export const getCurrentUserClientId = (state) => state.getIn(['app', 'currentUser', 'clientId']);
export const getCurrentUserEmail = (state) => state.getIn(['app', 'currentUser', 'email']);
export const getIsCurrentUserEmailVerified = (state) => !!state.getIn(['app', 'currentUser', 'emailVerifiedDate']);
export const getCurrentUserRootBoardId = (state) => state.getIn(['app', 'currentUser', 'rootBoardId']);
export const isCurrentUserFetched = (state) => state.getIn(['app', 'currentUser', 'fetched']);
export const getCurrentUserTemplateRootBoardId = (state) => state.getIn(['app', 'currentUser', 'templateRootId']);
export const getCurrentUserQuickNotesRootBoardId = (state) => state.getIn(['app', 'currentUser', 'quickNotesRootId']);
export const isGuestSelector = (state) => state.getIn(['app', 'currentUser', 'isGuest']);
export const getLastCheckedQuickNotes = (state) => state.getIn(['app', 'currentUser', 'lastCheckedQuickNotes']);

export const getCurrentUserSegment = (state) => state.getIn(['app', 'currentUser', 'userSegment', 'segment']);

/**
 * @deprecated Since 16/09/22. Use `getCurrentUserSegment` instead (though this is required for existing user data)
 */
export const getCurrentUserSegments = (state) => state.getIn(['app', 'currentUser', 'segments']);

export const getCurrentUserLastViewedBoardId = (state) => state.getIn(['app', 'currentUser', 'lastViewedBoard']);
export const getCurrentUserEducation = (state) => state.getIn(['app', 'currentUser', 'education']);
export const getCurrentUserAccountCreationDate = (state) => state.getIn(['app', 'currentUser', 'accountCreationDate']);
export const getCurrentUserOTToken = (state) => state.getIn(['app', 'currentUser', 'otToken']);
export const getCurrentUserAccountRegistrationDate = propIn(['app', 'currentUser', 'accountRegistrationDate']);
export const getCurrentUserInitialisationLastViewedBoardId = (state) =>
    state.getIn(['app', 'currentUser', 'initialisationLastViewedBoardId']);
export const isCurrentUserSettingDrawingInkEffectEnabled = (state) =>
    getUserEnableDrawingInkEffect(state.getIn(['app', 'currentUser']));

export const getViewedBoardsSelector = (state) => state.getIn(['app', 'currentUser', 'viewedBoards']);

export const getCurrentUserRole = propIn(['app', 'currentUser', 'role']);
export const isCurrentUserStaff = (state) => isStaff(getCurrentUserRole(state));

export const isUserSubscribed = (state) => state.getIn(['app', 'currentUser', 'subscription', 'active']);
export const getCurrentUserSubscriptionId = (state) => state.getIn(['app', 'currentUser', 'subscriptionId']);
export const getCurrentUserSubscription = (state) => state.getIn(['app', 'currentUser', 'subscription']);
export const getCurrentUserSubscriptionDetail = (state) =>
    state.getIn(['app', 'currentUser', 'subscription', 'detail']);
export const getCurrentUserCustomer = (state) => state.getIn(['app', 'currentUser', 'customer']);

export const getSortedViewedBoardIds = createSelector(
    getViewedBoardsSelector,
    flow(
        (immutableMap) => immutableMap.toJS(),
        mapWithKey((timestamp, id) => ({ id, timestamp })),
        sortBy((entry) => entry.timestamp * -1),
        map('id'),
    ),
);

export const getIsDoubleClickToCreateNotesSettingEnabled = createSelector(getCurrentUser, (currentUser) =>
    getUserEnableDoubleClickToCreateNotes(currentUser),
);

/**
 * This filters out any viewed board that has since been deleted.
 */
export const getSortedViewableViewedBoardIds = createSelector(
    getSortedViewedBoardIds,
    getElements,
    (viewedBoardIds, elements) => viewedBoardIds.filter((boardId) => !isElementOrAncestorInTrash(elements, boardId)),
);

export const getPreviousViewedBoardId = createSelector(
    getSortedViewedBoardIds,
    getCurrentBoardId,
    (viewedBoards, currentBoardId) => {
        if (!viewedBoards || viewedBoards.length <= 1) return null;
        // Unfortunately the order of the sorted viewed boards isn't quite correct.
        // The currently viewed board is at index 1 and the previously viewed board is at position 0
        // The rest of the boards are in order of most recently viewed.
        // This is due to the selector recording viewed timestamps when navigating out of a board.
        return viewedBoards[0] === currentBoardId ? viewedBoards[1] : viewedBoards[0];
    },
);

export const currentUserIsSubscriptionAdminSelector = createSelector(
    getCurrentUser,
    getCurrentUserSubscription,
    (currentUser, subscription) => isUserSubscriptionAdmin(currentUser, subscription),
);

export const subscriptionTeamSelector = createSelector(getCurrentUserSubscription, getUsers, (subscription, users) => {
    const memberIds = subscription && subscription.get('teamMemberIds');
    if (!memberIds) return null;

    return memberIds.map((userId) => users.get(userId)).filter((user) => user);
});

export const subscriptionAdminUserSelector = createSelector(
    getCurrentUserSubscription,
    getUsers,
    (subscription, users) => {
        if (!users || !subscription) return;

        const adminId = subscription.get('adminId');
        return users.get(adminId);
    },
);

export const isUserAvailableSelector = createSelector(
    getCurrentUser,
    isGuestSelector,
    (currentUser, isGuest) => isGuest || (currentUser.get('authenticated') && currentUser.get('fetched')) || false,
);
